import {
    Box, Divider, Skeleton, Text as ChakraText
} from '@chakra-ui/react'
import moment from 'moment'
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { DateSelector, Usage } from '../../components/Analytics'
import TotalStats from '../../components/Analytics/TotalStats'
import type { AnalyticsAPIResponse, BandwidthGraphData } from '../../interfaces'
import { UserContext } from '../../providers'

const formatDate = (date: Date): string => moment(date).format()
const subtractDates = (date1, date2): number => {
    const diffTime = Math.abs(date2 - date1)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays + 1
}

const LoadingSkeleton = (): React.ReactElement => {
    return (
        <Skeleton borderRadius={"8px"} height={"300px"} width={"full"} />
    );
}

export const UsageStats = (): React.ReactElement => {
    const { t } = useTranslation()
    const { user } = useContext(UserContext)
    const initialEndDate = moment().endOf('day').toDate()
    const initialStartDate = moment().startOf('month').toDate()

    const [startDate, setStartDate] = useState<Date>(initialStartDate)
    const [endDate, setEndDate] = useState<Date>(initialEndDate)
    const [disableApply, setDisableApply] = useState<boolean>(false)
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
    const [, setDateError] = useState<string | null>(null)

    const DATE_90_DAYS_ERROR = useMemo(
        () => t('date_90_error', 'You can select a maximum range of 90 days!'),
        [],
    )
    const DATE_BAD_ORDER_ERROR = useMemo(
        () => t('date_bad_error', 'Start date cannot be greater than the end date'),
        [],
    )

    const isUserOnImagekitCDN = user?.clientIsOnImageKitCDN

    const { data: staticAnalyticsData, get: getStaticAnalyticsDataFetch } =
        useFetch<AnalyticsAPIResponse>({ cachePolicy: CachePolicies.NO_CACHE })

    const { data: analyticsData, get: getAnalyticsDataFetch } =
        useFetch<AnalyticsAPIResponse>({ cachePolicy: CachePolicies.NO_CACHE })

    const getAnalyticsData = useMemo(
        () => async (): Promise<void> => {
            if (user?.clientNumber) {
                setIsRefreshing(true)
                setDisableApply(true)

                await getAnalyticsDataFetch(
                    `/api/graphs/${user.clientNumber}/${formatDate(
                        startDate,
                    )}/${formatDate(endDate)}`,
                )

                setIsRefreshing(false)
                setDisableApply(false)
            }
        },
        [user, startDate, endDate],
    )

    const getStaticAnalyticsData = useMemo(
        () => async (): Promise<void> => {
            if (user?.clientNumber) {
                const localEndDate = moment().endOf('day').toDate()
                const localStartDate = moment().startOf('month').toDate()
                await getStaticAnalyticsDataFetch(
                    `/api/graphs/${user.clientNumber}/${formatDate(
                        localStartDate,
                    )}/${formatDate(localEndDate)}`,
                )
            }
        },
        [user],
    )

    useEffect(() => {
        void getStaticAnalyticsData()
        void getAnalyticsData()
        window.trackEvent({ label: 'DashboardAccessed' })
    }, [user])

    useEffect(() => {
        const difference = subtractDates(startDate, endDate)
        if (difference > 90) {
            setDateError(DATE_90_DAYS_ERROR)
            setDisableApply(true)
            return
        }

        if (startDate > endDate) {
            setEndDate(startDate)
        }

        setDateError(null)
        setDisableApply(false)
    }, [startDate])

    useEffect(() => {
        if (startDate > endDate) {
            setDateError(DATE_BAD_ORDER_ERROR)
            setDisableApply(true)
            return
        }

        const difference = subtractDates(startDate, endDate)
        if (difference > 90) {
            setDateError(DATE_90_DAYS_ERROR)
            setDisableApply(true)
            return
        }
        setDateError(null)
        setDisableApply(false)
    }, [endDate])

    const handleDateSubmit = useCallback(() => {
        void getAnalyticsData()
    }, [getAnalyticsData])

    const [bandwidthGraphData, setBandwidthGraphData] =
        useState<BandwidthGraphData | null>(null)

    if (!(analyticsData && staticAnalyticsData)) return <LoadingSkeleton />;

    return (
        <Box w={"full"}>
            {analyticsData && staticAnalyticsData ? (
                <Box marginTop="1rem" width="100%">
                    <ChakraText
                        fontWeight="semibold"
                        marginBottom="0.75rem"
                        fontSize="16px"
                    >
                        {t('usage_statistics', 'Usage Statistics')}
                    </ChakraText>

                    <Divider />

                    <Box marginTop="1rem">
                        <DateSelector
                            startDate={startDate}
                            setStartDate={setStartDate}
                            endDate={endDate}
                            setEndDate={setEndDate}
                            handleDateSubmit={handleDateSubmit}
                            disabled={disableApply || isRefreshing}
                            isRefreshing={isRefreshing}
                        />

                        <Box marginTop="1.5rem">
                            <TotalStats data={analyticsData} />
                            {isUserOnImagekitCDN && (
                                <Usage
                                    {...{
                                        data: analyticsData,
                                        bandwidthGraphData,
                                        setBandwidthGraphData,
                                        dateRange: {
                                            startDate,
                                            endDate,
                                        },
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>
            ) : null}
        </Box>
    )
}
